import { AppRoutes } from "@/routes/config";
import { RouteObject } from "react-router-dom";

import { LoginLayout, LoginLayoutWithGuest } from "@/components/layout/LoginLayout";
import { MatchLayoutWithAuth } from "@/components/layout/MatchLayout/MatchLayout";
import { SharedLayout } from "@/components/layout/SharedLayout";
import { CaseLayout } from "@/features/case/layouts/CaseLayout";
import { CompanyLayout } from "@/features/company/layout/CompanyLayout";
import { JobLayout } from "@/features/job/layout/JobLayout";
import { MaintenancePage } from "@/features/maintenance/pages/Maintenance";
import { MatchLayout } from "@/features/match/layout/MatchLayout";
import { PartnerLayout } from "@/features/partner/layout/PartnerLayout";
import { SharedDetailPage } from "@/features/sharing/pages/SharedDetailPage";
import { SharedPasswordPage } from "@/features/sharing/pages/SharedPasswordPage";
import { TalentLayout } from "@/features/talent/layout/TalentLayout";
import { UserSettingLayout } from "@/features/user-setting/layout/UserSettingLayout";
import AuthCallback from "@/pages/auth/AuthCallback";
import Login from "@/pages/auth/Login";
import BusinessReport from "@/pages/business-report/BusinessReport";
import { CaseCreate } from "@/pages/case/CaseCreate";
import { CaseEdit } from "@/pages/case/CaseEdit";
import { CaseList } from "@/pages/case/CaseList";
import { CompanyCreatePage } from "@/pages/company/CompanyCreate";
import CompanyDetailPage from "@/pages/company/CompanyDetail";
import { CompanyEditPage } from "@/pages/company/CompanyEdit";
import CompanyImport from "@/pages/company/CompanyImport";
import CompanyList from "@/pages/company/CompanyList";
import Contact from "@/pages/contact/Contact";
import Dashboard from "@/pages/dashboard/Dashboard";
import OfferAcceptedList from "@/pages/dashboard/OfferAcceptedList";
import PerformanceDetail from "@/pages/dashboard/PerformanceDetail";
import InformationDetail from "@/pages/information/InformationDetail";
import InformationList from "@/pages/information/InformationList";
import { JobCreatePage } from "@/pages/job/JobCreate";
import JobDetailPage from "@/pages/job/jobDetail";
import { JobEditPage } from "@/pages/job/JobEdit";
import JobImport from "@/pages/job/JobImport";
import JobList from "@/pages/job/JobList";
import ManagementLedger from "@/pages/management-ledger/ManagementLedger";
import Match from "@/pages/match/Match";
import MatchEdit from "@/pages/match/MatchEdit";
import PageNotFound from "@/pages/PageNotFound";
import PartnerCreate from "@/pages/partner/PartnerCreate";
import PartnerDetail from "@/pages/partner/PartnerDetail";
import PartnerEdit from "@/pages/partner/PartnerEdit";
import PartnerImport from "@/pages/partner/PartnerImport";
import PartnerList from "@/pages/partner/PartnerList";
import ServerErrorPage from "@/pages/ServerErrorPage";
import { TalentCreatePage } from "@/pages/talent/TalentCreate";
import TalentDetailPage from "@/pages/talent/TalentDetail";
import { TalentEditPage } from "@/pages/talent/TalentEdit";
import TalentImport from "@/pages/talent/TalentImport";
import TalentList from "@/pages/talent/TalentList";
import UserSettingChangeEmail from "@/pages/user-setting/UserSettingChangeEmail";
import UserSettingDetail from "@/pages/user-setting/UserSettingDetail";
import UserSettingEdit from "@/pages/user-setting/UserSettingEdit";
import { AppRouteNames } from "@/routes/constants";

export const routes: RouteObject[] = [
  {
    element: <LoginLayoutWithGuest />,
    children: [
      {
        path: AppRoutes.login,
        element: <Login />,
      },
    ],
  },
  {
    // Not guest-guarded: the handoff must run even when a stale session is still
    // around — the callback clears it before exchanging the token.
    element: <LoginLayout />,
    children: [
      {
        path: AppRoutes.authCallback,
        element: <AuthCallback />,
      },
    ],
  },
  {
    element: <MatchLayoutWithAuth />,
    children: [
      {
        path: AppRoutes.home,
        element: <Dashboard />,
        id: AppRouteNames.home.value,
      },
      {
        path: AppRoutes.offerAcceptedList,
        element: <OfferAcceptedList />,
        id: AppRouteNames.offerAcceptedList.value,
      },
      {
        path: AppRoutes.performanceDetail,
        element: <PerformanceDetail />,
        id: AppRouteNames.performanceDetail.value,
      },
      { path: AppRoutes.information, element: <InformationList />, id: AppRouteNames.information.value },
      { path: AppRoutes.informationDetail, element: <InformationDetail />, id: AppRouteNames.informationDetail.value },
      {
        path: AppRoutes.talentList,
        element: <TalentList />,
        id: AppRouteNames.talentList.value,
      },
      {
        element: <TalentLayout />,
        children: [
          { path: AppRoutes.talentCreate, element: <TalentCreatePage />, id: AppRouteNames.talentCreate.value },
          { path: AppRoutes.talentEdit, element: <TalentEditPage />, id: AppRouteNames.talentEdit.value },
          { path: AppRoutes.talentDetail, element: <TalentDetailPage />, id: AppRouteNames.talentDetail.value },
        ],
      },
      {
        children: [{ path: AppRoutes.talentImport, element: <TalentImport />, id: AppRouteNames.talentImport.value }],
      },
      { path: AppRoutes.contact, element: <Contact />, id: AppRouteNames.contact.value },
      { path: AppRoutes.companyList, element: <CompanyList />, id: AppRouteNames.companyList.value },
      {
        element: <CompanyLayout />,
        children: [
          { path: AppRoutes.companyCreate, element: <CompanyCreatePage />, id: AppRouteNames.companyCreate.value },
          { path: AppRoutes.companyEdit, element: <CompanyEditPage />, id: AppRouteNames.companyEdit.value },
          { path: AppRoutes.companyDetail, element: <CompanyDetailPage />, id: AppRouteNames.companyDetail.value },
          { path: AppRoutes.companyImport, element: <CompanyImport />, id: AppRouteNames.companyImport.value },
        ],
      },
      { path: AppRoutes.caseList, element: <CaseList />, id: AppRouteNames.caseList.value },
      {
        element: <CaseLayout />,
        children: [
          { path: AppRoutes.caseCreate, element: <CaseCreate />, id: AppRouteNames.caseCreate.value },
          { path: AppRoutes.caseEdit, element: <CaseEdit />, id: AppRouteNames.caseEdit.value },
        ],
      },
      {
        element: <UserSettingLayout />,
        children: [
          { path: AppRoutes.userSettingDetail, element: <UserSettingDetail />, id: AppRouteNames.userSettingDetail.value },
          { path: AppRoutes.userSettingEdit, element: <UserSettingEdit />, id: AppRouteNames.userSettingEdit.value },
          { path: AppRoutes.userSettingChangeEmail, element: <UserSettingChangeEmail />, id: AppRouteNames.userSettingChangeEmail.value },
        ],
      },
      {
        element: <MatchLayout />,
        children: [
          { path: AppRoutes.match, element: <Match />, id: AppRouteNames.match.value },
          { path: AppRoutes.matchEdit, element: <MatchEdit />, id: AppRouteNames.matchEdit.value },
        ],
      },
      { path: AppRoutes.jobList, element: <JobList />, id: AppRouteNames.jobList.value },
      {
        element: <JobLayout />,
        children: [
          { path: AppRoutes.jobCreate, element: <JobCreatePage />, id: AppRouteNames.jobCreate.value },
          { path: AppRoutes.jobDetail, element: <JobDetailPage />, id: AppRouteNames.jobDetail.value },
          { path: AppRoutes.jobEdit, element: <JobEditPage />, id: AppRouteNames.jobEdit.value },
        ],
      },
      {
        children: [{ path: AppRoutes.jobImport, element: <JobImport />, id: AppRouteNames.jobImport.value }],
      },
      { path: AppRoutes.partnerList, element: <PartnerList />, id: AppRouteNames.partnerList.value },
      {
        element: <PartnerLayout />,
        children: [
          { path: AppRoutes.partnerCreate, element: <PartnerCreate />, id: AppRouteNames.partnerCreate.value },
          { path: AppRoutes.partnerDetail, element: <PartnerDetail />, id: AppRouteNames.partnerDetail.value },
          { path: AppRoutes.partnerEdit, element: <PartnerEdit />, id: AppRouteNames.partnerEdit.value },
        ],
      },
      { path: AppRoutes.partnerImport, element: <PartnerImport />, id: AppRouteNames.partnerImport.value },
      { path: AppRoutes.businessReport, element: <BusinessReport />, id: AppRouteNames.businessReport.value },
      { path: AppRoutes.managementLedger, element: <ManagementLedger />, id: AppRouteNames.managementLedger.value },
    ],
  },
  { path: AppRoutes.serverErrorPage, element: <ServerErrorPage /> },
  { path: AppRoutes.maintenance, element: <MaintenancePage /> },
  {
    element: <SharedLayout />,
    children: [
      { path: AppRoutes.sharedPassword, element: <SharedPasswordPage /> },
      { path: AppRoutes.sharedDetail, element: <SharedDetailPage /> },
    ],
  },
  { path: "*", element: <PageNotFound /> },
];

const flattenRoutes = (routes: RouteObject[]): RouteObject[] => {
  const flatRoutes: RouteObject[] = [];

  const extractChildren = (route: RouteObject) => {
    if (route.children) {
      route.children.forEach((child) => {
        extractChildren(child); // Recursively extract children
      });
    } else {
      flatRoutes.push(route); // Push the route if there are no children
    }
  };

  routes.forEach((route) => extractChildren(route));

  return flatRoutes;
};

export const flattenedRoutes = flattenRoutes(routes);
